import React from 'react';
import { ArrowUpRightIcon, ArrowDownLeftIcon, PlusIcon, MinusIcon } from 'lucide-react';
import { Transaction } from '../types/wallet';
interface TransactionListProps {
  transactions: Transaction[];
  maxItems?: number;
}
export function TransactionList({
  transactions,
  maxItems
}: TransactionListProps) {
  const displayTransactions = maxItems ? transactions.slice(0, maxItems) : transactions;
  const getTransactionIcon = (type: Transaction['type']) => {
    switch (type) {
      case 'received':
        return <ArrowDownLeftIcon className="w-5 h-5 text-green-600" data-id="element-131" />;
      case 'sent':
        return <ArrowUpRightIcon className="w-5 h-5 text-red-600" data-id="element-132" />;
      case 'topup':
        return <PlusIcon className="w-5 h-5 text-blue-600" data-id="element-133" />;
      case 'withdrawal':
        return <MinusIcon className="w-5 h-5 text-orange-600" data-id="element-134" />;
    }
  };
  const getTransactionColor = (type: Transaction['type']) => {
    switch (type) {
      case 'received':
        return 'text-green-600';
      case 'sent':
        return 'text-red-600';
      case 'topup':
        return 'text-blue-600';
      case 'withdrawal':
        return 'text-orange-600';
    }
  };
  const getTransactionBg = (type: Transaction['type']) => {
    switch (type) {
      case 'received':
        return 'bg-green-50';
      case 'sent':
        return 'bg-red-50';
      case 'topup':
        return 'bg-blue-50';
      case 'withdrawal':
        return 'bg-orange-50';
    }
  };
  const formatAmount = (amount: number, type: Transaction['type']) => {
    const formatted = new Intl.NumberFormat('fr-FR').format(amount);
    const prefix = type === 'received' || type === 'topup' ? '+' : '-';
    return `${prefix}${formatted} FCFA`;
  };
  const formatDate = (date: Date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } else if (diffDays === 1) {
      return 'Hier';
    } else if (diffDays < 7) {
      return `Il y a ${diffDays} jours`;
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short'
      });
    }
  };
  const getTransactionTitle = (transaction: Transaction) => {
    switch (transaction.type) {
      case 'received':
        return transaction.senderName || 'Paiement reçu';
      case 'sent':
        return transaction.recipientName || 'Paiement envoyé';
      case 'topup':
        return 'Recharge';
      case 'withdrawal':
        return 'Retrait';
    }
  };
  if (displayTransactions.length === 0) {
    return <div className="text-center py-12" data-id="element-135">
        <p className="text-gray-500" data-id="element-136">Aucune transaction</p>
      </div>;
  }
  return <div className="space-y-3" data-id="element-137">
      {displayTransactions.map(transaction => <div key={transaction.id} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-4" data-id="element-138">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${getTransactionBg(transaction.type)}`} data-id="element-139">
            {getTransactionIcon(transaction.type)}
          </div>

          <div className="flex-1 min-w-0" data-id="element-140">
            <h3 className="font-semibold text-gray-900 truncate" data-id="element-141">
              {getTransactionTitle(transaction)}
            </h3>
            <p className="text-sm text-gray-500" data-id="element-142">
              {transaction.description || formatDate(transaction.timestamp)}
            </p>
          </div>

          <div className="text-right" data-id="element-143">
            <p className={`font-bold ${getTransactionColor(transaction.type)}`} data-id="element-144">
              {formatAmount(transaction.amount, transaction.type)}
            </p>
            <p className="text-xs text-gray-400" data-id="element-145">
              {formatDate(transaction.timestamp)}
            </p>
          </div>
        </div>)}
    </div>;
}